/**
 * Pure zoom and pan arithmetic for `ExpandView.tsx` — the demo's `EXPZ`,
 * `expZoom` and `expReset` (demo lines 1916, 1925–1927).
 *
 * The view is `translate(x, y) scale(k)` with the transform origin at the top
 * left, so a content point `c` sits on screen at `x + k·c`.
 */

/** The overlay's transform. The demo's `EXPZ`. */
export interface ExpandZoom {
  readonly k: number;
  readonly x: number;
  readonly y: number;
}

/** Demo line 1926 — zoom is clamped to 40 %…400 %. */
export const EXPAND_ZOOM_MIN = 0.4;
export const EXPAND_ZOOM_MAX = 4;

/** Demo line 1932 — one wheel notch or button press is ×1.12. */
export const EXPAND_ZOOM_STEP = 1.12;

/** Demo line 1927 — `expReset`. */
export const EXPAND_ZOOM_IDENTITY: ExpandZoom = { k: 1, x: 0, y: 0 };

/**
 * Multiply the zoom by `factor`, keeping the screen point `(px, py)` — the
 * pointer, or the top centre for the buttons — fixed under the zoom.
 *
 * The demo scaled about the element's centre and left the translation alone;
 * anchoring on the pointer is what makes wheel zoom land on the chart under
 * the cursor rather than drifting away from it.
 */
export function zoomAt(z: ExpandZoom, factor: number, px: number, py: number): ExpandZoom {
  const k = Math.max(EXPAND_ZOOM_MIN, Math.min(EXPAND_ZOOM_MAX, +(z.k * factor).toFixed(3)));
  if (k === z.k) return z;
  const ratio = k / z.k;
  return { k, x: px - (px - z.x) * ratio, y: py - (py - z.y) * ratio };
}

/** Shift the view by a drag delta. */
export function panBy(z: ExpandZoom, dx: number, dy: number): ExpandZoom {
  return { k: z.k, x: z.x + dx, y: z.y + dy };
}

/** The zoom readout — the demo's `#exp-pct`. */
export function zoomPercent(z: ExpandZoom): string {
  return `${Math.round(z.k * 100)}%`;
}
